import { mean, median } from '../geometry/statistics.js';
import { clamp, type NormalizedRect } from '../types/geometry.js';
import type { CheckRunContext } from '../types/checks.js';
import type { AuthenticitySignal, AuthenticitySignalId } from './index.js';
import { faceRegion, backgroundTextureStats, roiTextureStats } from './skinTexture.js';

/**
 * Signals F (subject boundary / halo) and G (noise mismatch between the skin
 * and the background).
 *
 * Both are the classic fingerprint of a replaced background: the subject keeps
 * its camera noise while the new background is perfectly smooth, and the
 * silhouette becomes an unnaturally clean cut with a halo where the two were
 * blended. Neither is proof — a clean studio backdrop looks similar — so the
 * signals carry a moderate confidence and the check never fails by default.
 */

const SAMPLES = 64;

const clamp01 = (value: number): number => clamp(value, 0, 1);

interface LocalPatch {
  mean: number;
  std: number;
  gradient: number;
}

/** 3×3 statistics around a clamped pixel position. */
const patchAt = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  x: number,
  y: number,
): LocalPatch => {
  const cx = clamp(Math.round(x), 1, width - 2);
  const cy = clamp(Math.round(y), 1, height - 2);
  let sum = 0;
  let count = 0;
  for (let dy = -1; dy <= 1; dy += 1) {
    for (let dx = -1; dx <= 1; dx += 1) {
      sum += luma[(cy + dy) * width + (cx + dx)] as number;
      count += 1;
    }
  }
  const localMean = count > 0 ? sum / count : 0;
  let squared = 0;
  for (let dy = -1; dy <= 1; dy += 1) {
    for (let dx = -1; dx <= 1; dx += 1) {
      const delta = (luma[(cy + dy) * width + (cx + dx)] as number) - localMean;
      squared += delta * delta;
    }
  }
  const gradient =
    Math.abs((luma[cy * width + cx + 1] as number) - (luma[cy * width + cx - 1] as number)) +
    Math.abs((luma[(cy + 1) * width + cx] as number) - (luma[(cy - 1) * width + cx] as number));
  return { mean: localMean, std: Math.sqrt(squared / Math.max(1, count)), gradient };
};

const unavailable = (id: AuthenticitySignal['id'], note: string): AuthenticitySignal => ({
  id,
  score: 0,
  confidence: 0.02,
  note,
});

/**
 * F — halo / edge mismatch along the subject silhouette.
 *
 * The face ellipse is sampled at 64 angles. Just inside and just outside the
 * boundary we compare the local texture, the boundary gradient and how far the
 * outer samples deviate from the background luma. A pasted subject shows a
 * noisy inside against an abnormally smooth outside (or a consistent halo ring).
 */
export const boundarySignal = (ctx: CheckRunContext): AuthenticitySignal => {
  const pixels = ctx.pixels;
  const faceBox = ctx.geometry?.faceBox;
  if (!pixels || !faceBox) {
    return unavailable('boundary', 'Нет геометрии лица — контур субъекта не проверить.');
  }
  const { luma, width, height } = pixels;
  const centreX = (faceBox.x + faceBox.width / 2) * width;
  const centreY = (faceBox.y + faceBox.height / 2) * height;
  const radiusX = (faceBox.width / 2) * width;
  const radiusY = (faceBox.height / 2) * height;
  if (radiusX < 6 || radiusY < 6) {
    return unavailable('boundary', 'Лицо слишком мало для анализа контура.');
  }

  const outerLumas: number[] = [];
  const innerStds: number[] = [];
  const outerStds: number[] = [];
  const boundaryGradients: number[] = [];
  const headBox = ctx.geometry?.headBox;
  let used = 0;

  for (let index = 0; index < SAMPLES; index += 1) {
    const angle = (index / SAMPLES) * Math.PI * 2;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const outerX = centreX + cos * radiusX * 1.18;
    const outerY = centreY + sin * radiusY * 1.18;

    /*
     * Just outside the *face* box there is often the person's own hair, not the
     * background. Sampling the hair would report a dark "halo" on every normal
     * portrait, so those angles are skipped and only the jaw/cheek silhouette —
     * where the outside really is background — is judged.
     */
    if (headBox) {
      const nx = outerX / width;
      const ny = outerY / height;
      const insideHead =
        nx >= headBox.x - 0.01 &&
        nx <= headBox.x + headBox.width + 0.01 &&
        ny >= headBox.y - 0.01 &&
        ny <= headBox.y + headBox.height + 0.01;
      if (insideHead) continue;
    }

    const inner = patchAt(
      luma,
      width,
      height,
      centreX - cos * radiusX * 0.82,
      centreY - sin * radiusY * 0.82,
    );
    const outer = patchAt(luma, width, height, outerX, outerY);
    const boundary = patchAt(luma, width, height, centreX + cos * radiusX, centreY + sin * radiusY);
    innerStds.push(inner.std);
    outerStds.push(outer.std);
    outerLumas.push(outer.mean);
    boundaryGradients.push(boundary.gradient);
    used += 1;
  }

  if (used < 12) {
    return unavailable(
      'boundary',
      'Слишком мало участков контура вне волос для оценки силуэта.',
    );
  }

  /*
   * The face mesh box reaches into the hairline, so a few "inside" probes land
   * on hair and would look like a hard inside/outside mismatch. Probes whose
   * local texture is far above the session median are therefore discarded
   * before the means are formed.
   */
  const medianInner = median(innerStds);
  const innerLimit = Math.max(15, medianInner * 4);
  const kept: number[] = [];
  for (let index = 0; index < used; index += 1) {
    if ((innerStds[index] as number) <= innerLimit) kept.push(index);
  }
  if (kept.length < 12) {
    return unavailable('boundary', 'Контур субъекта перекрыт волосами — силуэт не оценить.');
  }

  const innerStd = mean(kept.map((index) => innerStds[index] as number));
  const outerStd = mean(kept.map((index) => outerStds[index] as number));
  const edgeGradient = mean(kept.map((index) => boundaryGradients[index] as number));
  const keptOuterLumas = kept.map((index) => outerLumas[index] as number);
  const outerLuma = mean(keptOuterLumas);
  const backgroundLuma = ctx.background?.meanLuma ?? outerLuma;

  const uniformityRatio = innerStd / Math.max(outerStd, 0.6);
  const uniformScore = clamp01((uniformityRatio - 1.6) / 2.4);

  let haloCount = 0;
  let haloSign = 0;
  for (const value of keptOuterLumas) {
    const delta = value - backgroundLuma;
    if (Math.abs(delta) > 12) {
      haloCount += 1;
      haloSign += Math.sign(delta);
    }
  }
  const haloFraction = haloCount / kept.length;
  const consistency = haloCount > 0 ? Math.abs(haloSign) / haloCount : 0;
  const haloScore = clamp01(haloFraction * consistency * 2.2);

  const edgeScore = clamp01((edgeGradient - 25) / 70);
  const score = clamp01(0.5 * uniformScore + 0.35 * haloScore + 0.15 * edgeScore);

  const confidence = clamp01(0.35 + (ctx.background ? 0.1 : 0) + (pixels.width >= 200 ? 0.15 : 0));

  return {
    id: 'boundary',
    score,
    confidence,
    note:
      score >= 0.35
        ? 'контур субъекта слишком ровный, а фон без шума — возможна вставка субъекта'
        : 'контур субъекта и фон выглядят естественно',
  };
};

/**
 * G — noise sigma in the skin ROI versus the background ROI.
 *
 * A real photo has one sensor-noise floor across the frame; a replaced
 * background usually does not. The comparison is meaningless when both ROIs are
 * essentially noiseless, so that case lowers the confidence instead of scoring.
 */
export const noiseMismatchSignal = (ctx: CheckRunContext): AuthenticitySignal => {
  const pixels = ctx.pixels;
  if (!pixels) return unavailable('noise_mismatch', 'Пиксельный буфер недоступен.');
  const skinRect = faceRegion(ctx);
  if (!skinRect) {
    return unavailable(
      'noise_mismatch',
      'Нет области кожи для сравнения шума (нужна геометрия лица).',
    );
  }

  const skin = roiTextureStats(pixels, skinRect);
  const surround = backgroundTextureStats(pixels, ctx);
  if (!skin.valid || !surround.valid) {
    return unavailable('noise_mismatch', 'Области лица или фона слишком малы для оценки шума.');
  }

  const maxSigma = Math.max(skin.noiseSigma, surround.noiseSigma);
  const minSigma = Math.min(skin.noiseSigma, surround.noiseSigma);
  if (maxSigma < 1) {
    return {
      id: 'noise_mismatch',
      score: 0,
      confidence: 0.15,
      note: 'Шум слишком низкий в обеих областях — сравнивать нечего.',
    };
  }

  const ratio = (maxSigma + 0.3) / (minSigma + 0.3);
  const score = clamp01((ratio - 2.2) / 6);
  const confidence = clamp01(0.35 + (maxSigma > 2 ? 0.2 : 0) + (skin.count > 400 ? 0.1 : 0));

  return {
    id: 'noise_mismatch',
    score,
    confidence,
    note:
      score >= 0.35
        ? 'уровень шума кожи и фона сильно различается'
        : 'уровень шума кожи и фона сопоставим',
  };
};

/** Which signals the background-replacement check combines, and their weights. */
export const BACKGROUND_SIGNAL_IDS: readonly AuthenticitySignalId[] = [
  'boundary',
  'noise_mismatch',
  'resampling',
];

export const BACKGROUND_WEIGHTS: Partial<Record<AuthenticitySignalId, number>> = {
  boundary: 0.38,
  noise_mismatch: 0.4,
  resampling: 0.22,
};

/**
 * The background band used for the overlay marker.
 *
 * The marker must point at the background, never at the face: the top or the
 * bottom band (whichever is larger) is used, and the face/head box is excluded
 * by construction.
 */
export const backgroundBand = (subject: NormalizedRect | undefined): NormalizedRect => {
  if (!subject) return { x: 0.02, y: 0.02, width: 0.96, height: 0.12 };
  const topHeight = clamp(subject.y - 0.02, 0, 1);
  const bottomStart = clamp(subject.y + subject.height + 0.02, 0, 1);
  const bottomHeight = 1 - bottomStart;
  if (topHeight >= bottomHeight && topHeight >= 0.06) {
    return { x: 0, y: 0, width: 1, height: topHeight };
  }
  if (bottomHeight >= 0.06) {
    return { x: 0, y: bottomStart, width: 1, height: bottomHeight };
  }
  return { x: 0.02, y: 0.02, width: 0.96, height: 0.12 };
};
